import { useRef } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TaskDeleteError } from '../errors';
import { useDeleteTask } from '../hooks/useTaskLifecycle';
import type { DeletionImpact } from '../taskDeletion';

/**
 * Confirms a task delete (spec 137 US2). Permanent, and it takes the task's
 * subtasks with it, so the description says how many and whether running work
 * will be stopped.
 *
 * Closing is blocked while the request is in flight: the service waits for
 * running work to stop, so the mutation always resolves in this dialog. A task
 * that is already gone is the outcome the person wanted, so it closes with a
 * toast; any other refusal stays inline for a retry.
 */
export function DeleteTaskDialog({
  workspaceId,
  taskId,
  taskTitle,
  impact,
  open,
  onOpenChange,
  onDeleted,
}: {
  workspaceId: string;
  taskId: string;
  taskTitle: string;
  impact: DeletionImpact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}) {
  const deleteMutation = useDeleteTask();
  const { error, isPending } = deleteMutation;
  // Two clicks can land before React re-renders with `isPending`, so the guard
  // that stops a second request has to be synchronous.
  const sending = useRef(false);
  // Not only the typed refusals: a network failure or anything unexpected must
  // not leave the dialog quiet. "Already gone" closes the dialog instead.
  const alreadyGone =
    error instanceof TaskDeleteError && error.kind === 'not-found';
  const failure =
    error === null || alreadyGone
      ? null
      : error instanceof TaskDeleteError
        ? error.message
        : 'The task could not be deleted. Check your connection and try again.';

  const close = () => {
    onOpenChange(false);
    deleteMutation.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    onOpenChange(next);
    if (!next) deleteMutation.reset();
  };

  const confirmDelete = () => {
    if (isPending || sending.current) return;
    sending.current = true;
    deleteMutation.mutate(
      { workspaceId, taskId },
      {
        onSettled: () => {
          sending.current = false;
        },
        onSuccess: () => {
          toast(`Deleted ${taskTitle}`);
          close();
          onDeleted?.();
        },
        onError: (mutationError) => {
          if (
            mutationError instanceof TaskDeleteError &&
            mutationError.kind === 'not-found'
          ) {
            toast.info(`${taskTitle} was already deleted`);
            close();
            onDeleted?.();
          }
        },
      },
    );
  };

  const { count, anyRunning } = impact;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {taskTitle}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the task
            {count > 0
              ? ` and ${count} ${count === 1 ? 'subtask' : 'subtasks'}`
              : ''}
            . This cannot be undone.
            {anyRunning ? ' Running work will be stopped.' : ''}
          </DialogDescription>
        </DialogHeader>

        {failure !== null && (
          <p role="alert" className="text-destructive text-sm">
            {failure}
          </p>
        )}

        <DialogFooter>
          <DialogClose
            render={<Button variant="outline" disabled={isPending} />}
          >
            Cancel
          </DialogClose>
          <Button
            variant="destructive"
            disabled={isPending}
            onClick={confirmDelete}
          >
            {isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
